import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, Wallet, Hash } from 'lucide-react';
import type { SearchTotals } from '../../services/search';

interface SearchResultsTotalsProps {
  totals: SearchTotals;
}

function formatAmount(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(value);
}

export default function SearchResultsTotals({ totals }: SearchResultsTotalsProps) {
  const { t, i18n } = useTranslation('search');
  const locale = i18n.language === 'en' ? 'en-GB' : 'fr-FR';

  const items = [
    {
      label: t('totals.count'),
      value: String(totals.count),
      icon: Hash,
      color: 'text-gray-600',
      bg: 'bg-gray-100',
    },
    {
      label: t('totals.credits'),
      value: formatAmount(totals.credits, locale),
      icon: TrendingUp,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
    },
    {
      label: t('totals.debits'),
      value: formatAmount(totals.debits, locale),
      icon: TrendingDown,
      color: 'text-rose-600',
      bg: 'bg-rose-50',
    },
    {
      label: t('totals.net'),
      value: formatAmount(totals.net, locale),
      icon: Wallet,
      color: totals.net >= 0 ? 'text-emerald-700' : 'text-rose-700',
      bg: totals.net >= 0 ? 'bg-emerald-50' : 'bg-rose-50',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <div
            key={item.label}
            className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3"
          >
            <div className={`w-10 h-10 rounded-lg ${item.bg} flex items-center justify-center shrink-0`}>
              <Icon className={`w-5 h-5 ${item.color}`} />
            </div>
            <div className="min-w-0">
              <div className="text-xs text-gray-500">{item.label}</div>
              <div className={`text-lg font-semibold truncate ${item.color}`}>{item.value}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
